import { Box, Checkbox, FormControlLabel } from "@mui/material";
import { Typography } from "@platform/design-system";

import Section from "./Section";
import SelectionActions from "./SelectionActions";
import { SendRfpCarrier } from "./api/types";

export interface CarriersSectionProps {
  carriers: SendRfpCarrier[];
  selectedCarriers: string[];
  onCarriersChange: (carriers: string[]) => void;
  eligibleEmployees: number | null;
  isOfflineSubmission: boolean;
  onNext?: () => void;
  active?: boolean;
  completed?: boolean;
}

const CarriersSection = ({
  carriers,
  selectedCarriers,
  onCarriersChange,
  eligibleEmployees,
  isOfflineSubmission,
  onNext,
  active = false,
  completed = false,
}: CarriersSectionProps): React.JSX.Element => {
  const connectedCarriers = carriers.filter((c) => c.status === "connected");
  const unconnectedCarriers = carriers.filter(
    (c) => c.status === "unconnected",
  );

  // A carrier is blocked from API submission when its group is at or above the
  // carrier's eligible-lives limit (supplied by the carriers endpoint), unless
  // the user opts into offline submission, which bypasses the carrier's API
  // entirely. Returns the message to display, or null when allowed. See ADR-037
  // (PB-690).
  const apiRestrictionMessage = (carrier: SendRfpCarrier): string | null => {
    if (isOfflineSubmission) return null;
    if (carrier.maxEligibleLives === null) return null;
    const isOverLimit = (eligibleEmployees ?? 0) >= carrier.maxEligibleLives;
    return isOverLimit ? carrier.restrictionMessage : null;
  };

  const handleSelectAll = (): void => {
    onCarriersChange(
      carriers
        .filter((c) => c.salesRepEmail && apiRestrictionMessage(c) === null)
        .map((c) => c.id),
    );
  };

  const handleClear = (): void => {
    onCarriersChange([]);
  };

  const handleToggleCarrier = (carrierId: string): void => {
    if (selectedCarriers.includes(carrierId)) {
      onCarriersChange(selectedCarriers.filter((id) => id !== carrierId));
    } else {
      onCarriersChange([...selectedCarriers, carrierId]);
    }
  };

  const renderCarrierCheckbox = (
    carrier: SendRfpCarrier,
  ): React.JSX.Element => {
    const isMissingSalesRep = !carrier.salesRepEmail;
    const isSelected = selectedCarriers.includes(carrier.id);
    const restrictionMessage = apiRestrictionMessage(carrier);
    const isApiRestricted = restrictionMessage !== null;
    // A restricted carrier that is already selected stays enabled so it can be
    // unchecked — only block selecting it — so an analyst can remove it when the
    // group grows past the limit after selection (PB-708).
    const isDisabled = isMissingSalesRep || (isApiRestricted && !isSelected);
    return (
      <Box
        key={carrier.id}
        sx={{ display: "flex", flexDirection: "column", pt: 1 }}
      >
        <FormControlLabel
          disabled={isDisabled}
          control={
            <Checkbox
              checked={isSelected}
              onChange={() => handleToggleCarrier(carrier.id)}
              disabled={isDisabled}
              sx={{
                color: "grey.400",
                "&.Mui-checked": {
                  color: "#7B1FA2",
                },
                mr: 3,
              }}
            />
          }
          label={carrier.name}
        />
        {isMissingSalesRep && (
          <Typography variant="body-medium" color="danger" sx={{ ml: 7 }}>
            missing sales rep email
          </Typography>
        )}
        {restrictionMessage && (
          <Typography
            variant="body-medium"
            color="textSecondary"
            sx={{ ml: 7 }}
          >
            {restrictionMessage}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Section
      title="Carriers"
      onNext={onNext}
      nextLabel="Next"
      nextDisabled={selectedCarriers.length === 0}
      active={active}
      completed={completed}
    >
      <Box sx={{ pl: "1em", pt: "1em" }}>
        <SelectionActions
          onSelectAll={handleSelectAll}
          onClear={handleClear}
          selectAllDisabled={!carriers.some((c) => c.salesRepEmail)}
          clearDisabled={!selectedCarriers.length}
        />

        <Box sx={{ pl: "1em", pt: "1em" }}>
          {/* Connected Carriers */}
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="body-medium"
              color="textSecondary"
              sx={{ mb: 1 }}
            >
              <strong>Connected</strong>
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {connectedCarriers.map(renderCarrierCheckbox)}
            </Box>
          </Box>

          {/* Unconnected Carriers */}
          {unconnectedCarriers.length > 0 && (
            <Box>
              <Typography
                variant="body-medium"
                color="textSecondary"
                sx={{ mb: 1 }}
              >
                <strong>Unconnected</strong>
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                {unconnectedCarriers.map(renderCarrierCheckbox)}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Section>
  );
};

export default CarriersSection;
